import { maxBy } from 'lodash'
import { formatUnits } from 'viem'
import { base } from 'viem/chains'

import { useActiveOrg, useActiveOrgId } from '~/hooks/org'
import { useOrgBalances } from '~/hooks/useBalances'
import { IAddress } from '~/types'
import { Address } from '~/utils/address'
import { getEarnDepositChainIds, getEarnDepositVaults } from '~/utils/vault'

/**
 * USDC each account can deposit to Earn, per network. Only covers networks the
 * org has enabled that take Earn deposits.
 */
export const useEarnDepositBalances = () => {
  const activeOrgId = useActiveOrgId()
  const { org, isLoading: isLoadingOrg } = useActiveOrg()
  const { balances, isLoading: isLoadingBalances } = useOrgBalances(activeOrgId)
  const chainIds = getEarnDepositChainIds().filter((chainId) =>
    org?.smartAccountsChainIds.includes(chainId),
  )

  const usdcBalances = Object.entries(balances).flatMap(([address, account]) =>
    account.balances.flatMap((balance) => {
      const chainId = chainIds.find((id) => id === balance.chainId)
      if (!chainId || !getEarnDepositVaults(chainId, balance.address).length)
        return []

      return [
        {
          address: Address.format(address),
          chainId,
          usdcBalance: Number(
            formatUnits(balance.amount, balance.tokenMetadata.decimals),
          ),
        },
      ]
    }),
  )

  // Deposit where the account (or, without one, any account) holds the most
  // USDC. Base is the last resort when the org has no Earn network enabled.
  const getDepositChainId = (account?: IAddress) =>
    maxBy(
      usdcBalances.filter(
        (balance) => !account || Address.equal(balance.address, account),
      ),
      'usdcBalance',
    )?.chainId ??
    chainIds.at(0) ??
    base.id

  return {
    usdcBalances,
    getDepositChainId,
    isLoading: isLoadingOrg || isLoadingBalances,
  }
}
